import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Paginator from '../paginator';
import { apiurl } from '../../utils/constants';
import Highcharts from 'highcharts';
import drilldown from 'highcharts/modules/drilldown';
import HighchartsReact from 'highcharts-react-official';
import { localDateToUtcSql } from '../../utils/functions';
drilldown(Highcharts);

// Gráfico de cambios de estado por mes (columnas apiladas).
// Tiene su propia query (independiente de loadSolped): trae las solicitudes cuyo
// requested/approved/rejected/clos caiga en el rango, y cuenta cada hito en su mes.
// `from`/`to` son 'YYYY-MM-DD' del filtro; `filterSolped` aplica CC/grupo/urgencia.
function StateChangesByMonth({ mail, role, user, from = '', to = '', filterSolped }) {

	// Colores de estado usados en la app (--red, --ora, --gra, --gre)
	const STATE_COLORS = { Solicitado: '#C00', Aprobado: '#F60', Rechazado: '#666', Cerrado: '#0A2' };
	const STATES = ['Solicitado', 'Aprobado', 'Rechazado', 'Cerrado'];
	const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const utcFrom = from ? localDateToUtcSql(from, '00:00:00') : '';
				const utcTo = to ? localDateToUtcSql(to, '23:59:59') : '';

				let where = '';
				if (role < 3) {
					where = `AND (
						submitter=${user}
						OR EXISTS (SELECT 1 FROM req__assi assi INNER JOIN sys__link link ON assi.posi=link.posi AND link.user=${user} WHERE role='soli' AND assi.cent=soli.cent)
						OR EXISTS (SELECT 1 FROM sys__link WHERE user=${user} AND posi IN (cent.ord1, cent.ord2, cent.ord3, cent.pur1, cent.pur2, cent.pur3))
						OR appr.user = ${user}
					)`;
				}

				const cols = ['requested', 'approved', 'rejected', 'clos'];
				const inRange = (c) => [
					utcFrom && `${c} >= '${utcFrom}'`,
					utcTo && `${c} <= '${utcTo}'`,
				].filter(Boolean).join(' AND ');
				const dateFilter = (utcFrom || utcTo)
					? `AND ( ${cols.map(c => `(${inRange(c)})`).join(' OR ')} )`
					: '';

				const response = await fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						user: mail,
						query: `
							SELECT soli.iden, soli.requested, soli.approved, soli.rejected, soli.clos,
								cent.name cc_name, soli.urge,
								JSON_ARRAYAGG(JSON_OBJECT('group', iitem.grou)) AS details
							FROM req__soli soli
							LEFT JOIN req__cent cent ON soli.cent = cent.code
							LEFT JOIN req__item ritem ON soli.iden = ritem.soli
							LEFT JOIN inv__item iitem ON ritem.item = iitem.code
							LEFT JOIN req__appr appr ON soli.iden = appr.soli
							WHERE ( submitter=${user} OR requester IS NOT NULL OR rejected IS NOT NULL OR approved IS NOT NULL OR clos IS NOT NULL )
							${where}
							${dateFilter}
							GROUP BY soli.iden, soli.cent, cent.name, soli.urge, soli.requested, soli.approved, soli.rejected, soli.clos
							ORDER BY soli.iden DESC;
						`
					})
				});

				if (response.ok && !cancelled) {
					const rows = await response.json();
					setData(rows.map(row => ({
						...row,
						details: typeof row.details === 'string' ? JSON.parse(row.details) : (row.details ?? []),
					})));
				}
			} catch (error) {
				console.log('Error en StateChangesByMonth load ', error);
			}
		};

		// debounce: si el usuario sigue cambiando la fecha, no dispara la query en cada tecla
		const timer = setTimeout(load, 500);
		return () => { cancelled = true; clearTimeout(timer); };
	}, [mail, role, user, from, to]);

	const options = useMemo(() => {
		const rows = filterSolped(data);
		const buckets = {};
		const add = (dateStr, state) => {
			if (!dateStr) return;
			const d = new Date(dateStr);
			if (isNaN(d.getTime())) return;
			// Solo cuenta la acción si ocurrió dentro del rango filtrado
			const day = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
			if ((from && day < from) || (to && day > to)) return;
			const k = day.slice(0, 7);
			if (!buckets[k]) buckets[k] = { Solicitado: 0, Aprobado: 0, Rechazado: 0, Cerrado: 0 };
			buckets[k][state]++;
		};

		rows.forEach(s => {
			add(s.requested, 'Solicitado');
			add(s.approved, 'Aprobado');
			add(s.rejected, 'Rechazado');
			add(s.clos, 'Cerrado');
		});

		const keys = Object.keys(buckets).sort();
		const sameYear = new Set(keys.map(k => k.split('-')[0])).size <= 1;
		const categories = keys.map(k => {
			const [y, m] = k.split('-');
			return sameYear ? MONTHS[Number(m) - 1] : `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Cambios de estado por mes' },
			xAxis: { categories },
			yAxis: { min: 0, title: { text: 'Solicitudes' } },
			legend: { reversed: true },
			plotOptions: { column: { stacking: 'normal' } },
			tooltip: {
				headerFormat: '<b>{point.x}</b><br/>',
				pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
			},
			series: STATES.map(st => ({
				name: st,
				color: STATE_COLORS[st],
				data: keys.map(k => buckets[k][st]),
			})),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, from, to, filterSolped]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Tiempo promedio (en días) entre estados.
// Recibe las solicitudes ya filtradas.
function TimeBetweenStates({ rows = [] }) {

	const options = useMemo(() => {
		const diffDays = (end, start) => {
			if (!end || !start) return null;
			const e = new Date(end), s = new Date(start);
			if (isNaN(e.getTime()) || isNaN(s.getTime())) return null;
			const d = (e - s) / 86400000;
			return d >= 0 ? d : null;
		};

		// Fecha de la última compra (orden enviada) de la solicitud
		const lastPurchase = (s) => {
			const dates = (s.ordes || [])
				.map(o => o.sent)
				.filter(Boolean)
				.map(x => new Date(x))
				.filter(d => !isNaN(d.getTime()));
			return dates.length ? new Date(Math.max(...dates)) : null;
		};

		const avg = (arr) => arr.length
			? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10
			: 0;

		const appr = [], buy = [], clos = [];
		rows.forEach(s => {
			const purchase = lastPurchase(s);

			// Tiempo de respuesta del revisor: aprobar y rechazar miden lo mismo.
			// (rechazar borra `requested`, así que esas solicitudes no aportan hasta
			//  que el backend preserve la fecha de envío).
			const dAppr = diffDays(s.approved, s.requested);
			if (dAppr !== null) appr.push(dAppr);
			const dReje = diffDays(s.rejected, s.requested);
			if (dReje !== null) appr.push(dReje);

			const dBuy = diffDays(purchase, s.approved);
			if (dBuy !== null) buy.push(dBuy);

			const dClos = diffDays(s.clos, purchase || s.approved);
			if (dClos !== null) clos.push(dClos);
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Tiempo entre estados' },
			xAxis: { categories: ['Aprobación', 'Compra', 'Cierre'] },
			yAxis: { min: 0, title: { text: 'Días promedio' } },
			legend: { enabled: false },
			tooltip: { pointFormat: '<b>{point.y} días</b> (promedio)' },
			plotOptions: { column: { colorByPoint: true } },
			colors: ['#F60', '#FC0', '#0A2'],
			series: [{
				name: 'Días promedio',
				data: [avg(appr), avg(buy), avg(clos)],
			}],
		};
	}, [rows]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Formatea un monto en CLP de forma compacta: $1,5B / $250M / $80K / $500
function fmtCLP(v) {
	const n = Number(v) || 0;
	const abs = Math.abs(n);
	const fmt = (x, suf) => `$${x.toLocaleString('es-CL', { maximumFractionDigits: 1 })}${suf}`;
	if (abs >= 1e9) return fmt(n / 1e9, 'B');
	if (abs >= 1e6) return fmt(n / 1e6, 'M');
	if (abs >= 1e3) return fmt(n / 1e3, 'K');
	return fmt(n, '');
}

// Gasto mensual: suma del monto solicitado de cada solicitud por mes (fecha `requested`).
// Query propia: el monto sale de una subconsulta sobre req__item (no depende de cuántas
// OC/SM tenga la solicitud), y req__soli se une solo para fecha, permisos y filtros.
// `filterSolped` aplica CC/grupo/urgencia. El azul de la app (--blu) se aclara según el tamaño.
function MonthlySpend({ mail, role, user, from = '', to = '', filterSolped }) {

	const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const utcFrom = from ? localDateToUtcSql(from, '00:00:00') : '';
				const utcTo = to ? localDateToUtcSql(to, '23:59:59') : '';

				let where = '';
				if (role < 3) {
					where = `AND (
						submitter=${user}
						OR EXISTS (SELECT 1 FROM req__assi assi INNER JOIN sys__link link ON assi.posi=link.posi AND link.user=${user} WHERE role='soli' AND assi.cent=soli.cent)
						OR EXISTS (SELECT 1 FROM sys__link WHERE user=${user} AND posi IN (cent.ord1, cent.ord2, cent.ord3, cent.pur1, cent.pur2, cent.pur3))
						OR appr.user = ${user}
					)`;
				}

				const dateFilter = [
					utcFrom && `AND soli.requested >= '${utcFrom}'`,
					utcTo && `AND soli.requested <= '${utcTo}'`,
				].filter(Boolean).join(' ');

				const response = await fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						user: mail,
						query: `
							SELECT soli.iden, soli.requested, cent.name cc_name, soli.urge,
								COALESCE(amt.totalValue, 0) totalValue,
								amt.details
							FROM req__soli soli
							LEFT JOIN req__cent cent ON soli.cent = cent.code
							LEFT JOIN req__appr appr ON soli.iden = appr.soli
							LEFT JOIN (
								SELECT ri.soli,
									SUM(ri.amou * ri.cost) totalValue,
									JSON_ARRAYAGG(JSON_OBJECT('group', ii.grou)) details
								FROM req__item ri
								LEFT JOIN inv__item ii ON ri.item = ii.code
								GROUP BY ri.soli
							) amt ON amt.soli = soli.iden
							WHERE soli.requested IS NOT NULL
							${where}
							${dateFilter}
							GROUP BY soli.iden, soli.requested, cent.name, soli.urge, amt.totalValue, amt.details
							ORDER BY soli.iden DESC;
						`
					})
				});

				if (response.ok && !cancelled) {
					const list = await response.json();
					setData(list.map(row => ({
						...row,
						details: typeof row.details === 'string' ? JSON.parse(row.details) : (row.details ?? []),
					})));
				}
			} catch (error) {
				console.log('Error en MonthlySpend load ', error);
			}
		};

		// debounce: si el usuario sigue cambiando la fecha, no dispara la query en cada tecla
		const timer = setTimeout(load, 500);
		return () => { cancelled = true; clearTimeout(timer); };
	}, [mail, role, user, from, to]);

	const options = useMemo(() => {
		const rows = filterSolped(data);

		// Azul definido en el CSS de la app (--blu); expande #rgb -> #rrggbb
		let blu = (getComputedStyle(document.documentElement).getPropertyValue('--blu')
			|| getComputedStyle(document.body).getPropertyValue('--blu')).trim();
		if (/^#[0-9a-f]{3}$/i.test(blu)) blu = '#' + blu.slice(1).split('').map(c => c + c).join('');

		const buckets = {};
		rows.forEach(s => {
			if (!s.requested) return;
			const d = new Date(s.requested);
			if (isNaN(d.getTime())) return;
			const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
			buckets[k] = (buckets[k] || 0) + (Number(s.totalValue) || 0);
		});

		const keys = Object.keys(buckets).sort();
		const max = Math.max(1, ...keys.map(k => buckets[k]));

		const points = keys.map(k => {
			const v = buckets[k];
			// barra grande -> azul intenso; barra pequeña -> azul claro
			const ratio = v / max;
			return { y: v, color: Highcharts.color(blu).brighten(0.55 * (1 - ratio)).get('rgb') };
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Gasto mensual' },
			xAxis: {
				categories: (() => {
					const sameYear = new Set(keys.map(k => k.split('-')[0])).size <= 1;
					return keys.map(k => {
						const [y, m] = k.split('-');
						return sameYear ? MONTHS[Number(m) - 1] : `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
					});
				})()
			},
			yAxis: {
				min: 0,
				title: { text: null },
				labels: { formatter() { return fmtCLP(this.value); } },
			},
			legend: { enabled: false },
			tooltip: { pointFormatter() { return `<b>${fmtCLP(this.y)}</b>`; } },
			series: [{ name: 'Gasto', data: points }],
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, filterSolped]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function ListStat({ onMenuItemClick }) {

	const { iden: user, role, mail} = useSelector((state) => state.user);
	const [solped, setSolped] = useState([]);

	const now = new Date();
	const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
	const [filtRequDateStart, setFiltRequDateStart] = useState(firstDayOfMonth);
	const [filtRequDateEnd, setFiltRequDateEnd] = useState(false);
	const [selectedState, setSelectedState] = useState(false);
	const [grous, setGrous] = useState([]);
	const [selectedGrou, setSelectedGrou] = useState('');
	const [centStats, setCentStats] = useState([]);
	const [selectedCent, setSelectedCent] = useState(false);
	const [selectedUrge, setSelectedUrge] = useState(false);
	const drilledRef = useRef(false);
	const [options, setOptions] = useState({
		chart: { type: 'pie' },
		title: {
			text: 'Estado de solicitudes'
		}
	});
	const [paginatedSolped, setPaginatedSolped] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalFilteredItems, setTotalFilteredItems] = useState(0);
	const [filteredSolped, setFilteredSolped] = useState([]);
	const itemsPerPage = 10;

	const filterSolped = useCallback((_items) => {
		let filteredItems = _items;

		if (selectedCent) {
			filteredItems = filteredItems.filter(item => item.cc_name === selectedCent);
		}

		if (selectedGrou) {
			filteredItems = filteredItems.map(item => ({
				...item,
				details: item.details.filter(detail => String(detail.group) === String(selectedGrou))
			})).filter(item => item.details.length > 0);
		}

		if (selectedUrge) {
			filteredItems = filteredItems.filter(item => item.urge);
		}

		return filteredItems;
	}, [selectedCent, selectedGrou, selectedUrge]);

	useEffect(() => {

		let filteredItems = solped;
		if (selectedState) {
			let statesToInclude = [];
			if (selectedState === 'Pendiente') {
				statesToInclude = ['Solicitado', 'Comprado', 'Comprando', 'Aprobado'];
			} else {
				statesToInclude.push(selectedState);
			}

			filteredItems = filteredItems.filter(item => statesToInclude.includes(item.stat));
		}

		// Aplicar filtros de CC y grupo
		filteredItems = filterSolped(filteredItems);

		let filteredDetails = filteredItems.flatMap(item => item.details);
		filteredDetails = filteredDetails.filter(detail => detail.amou !== null);

		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		setPaginatedSolped(filteredDetails.slice(startIndex, endIndex));
		setFilteredSolped(filteredDetails);


	}, [solped, currentPage, selectedState, selectedGrou, selectedCent, filterSolped]);

	useEffect(() => {
		setTotalFilteredItems(filteredSolped.length);
	}, [filteredSolped]);

	const loadSolped = useCallback(async () => {
		try {
			let where = '';

			const from = filtRequDateStart ? localDateToUtcSql(filtRequDateStart, '00:00:00') : '';
			const to = filtRequDateEnd ? localDateToUtcSql(filtRequDateEnd, '23:59:59') : '';

			if (role < 3) {
				where = `AND (
					submitter=${user} 
					OR EXISTS (SELECT 1 FROM req__assi assi INNER JOIN sys__link link ON assi.posi=link.posi AND link.user=${user} WHERE role='soli' AND assi.cent=soli.cent)
					OR EXISTS (SELECT 1 FROM sys__link WHERE user=${user} AND posi IN (cent.ord1, cent.ord2, cent.ord3, cent.pur1, cent.pur2, cent.pur3))
					OR appr.user = ${user}
				)`;
			}

			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `
						SELECT
						soli.iden,
						JSON_ARRAYAGG(
							JSON_OBJECT(
								'ritem_iden', ritem.iden,
								'item_code', iitem.code,
								'item_name', iitem.name,
								'text', ritem.text,
								'unit', iitem.unit,
								'amou', ritem.amou,
								'pend', ritem.pend,
								'cost', ritem.cost,
								'hand', iitem.hand,
								'item_codeaf', ritem.fixe,
								'delivered', COALESCE(move.move, 0)
								,'group', iitem.grou
							)
						) AS details,
						JSON_ARRAYAGG(
							JSON_OBJECT('entr', orde.entr, 'docu', orde.docu, 'sent', orde.sent, 'stat', orde.stat)
						) AS ordes,
						soli.cent, cent.name cc_name,entry,soli.docu,urge,
						SUM(ritem.amou * ritem.cost) AS totalValue,
						requester,req.name req_name,requested,
						submitter, submitted, sub.name sub_name,
						soli.approver,soli.approved,
						rejected,
						clos,
						(SELECT IF(SUM(ritem.amou * ritem.cost)<=ord1, 'ord1', IF(SUM(ritem.amou * ritem.cost)<=ord2, 'ord2', 'ord3') ) FROM req__tier) AS ord,
						appr.user appr,
						auth.hasLvl1 AS hasLvl1,
						auth.hasLvl2 AS hasLvl2,
						auth.hasLvl3 AS hasLvl3,
						ord1.user AS ord1,
						ord2.user AS ord2,
						ord3.user AS ord3
					FROM req__soli soli
					LEFT JOIN req__item ritem ON soli.iden = ritem.soli
					LEFT JOIN inv__item iitem ON ritem.item = iitem.code
					LEFT JOIN inv__stor stor ON ritem.item = stor.item AND stor.ware = iitem.ware
					LEFT JOIN sys__user req ON soli.requester = req.iden
					LEFT JOIN sys__user sub ON soli.submitter = sub.iden
					LEFT JOIN req__cent cent ON soli.cent = cent.code
					LEFT JOIN req__appr appr ON soli.iden = appr.soli
					LEFT JOIN ord__form orde ON soli.entry = orde.entr
					LEFT JOIN req__move move ON move.item = ritem.item AND move.entr = soli.entry AND move.type = 'exit'
					LEFT JOIN (
						SELECT soli, MAX(IF(level=1,approver,NULL)) hasLvl1, MAX(IF(level=2,approver,NULL)) hasLvl2, MAX(IF(level=3,approver,NULL)) hasLvl3 FROM req__auth GROUP BY soli
					) auth ON soli.iden=auth.soli
					LEFT JOIN (SELECT user, posi FROM sys__link WHERE user=${user}) AS ord1 ON ord1.posi=cent.ord1
					LEFT JOIN (SELECT user, posi FROM sys__link WHERE user=${user}) AS ord2 ON ord2.posi=cent.ord2
					LEFT JOIN (SELECT user, posi FROM sys__link WHERE user=${user}) AS ord3 ON ord3.posi=cent.ord3
					WHERE ( (requester IS NULL AND submitter=${user}) OR requester IS NOT NULL OR submitter=${user} OR rejected IS NOT NULL)
					${where}
					${(from || to) ? `AND ( ( 1 ${from ? `AND requested >= '${from}'` : ''} ${to ? `AND requested <= '${to}'` : ''} ) OR ( 1 ${from ? `AND rejected >= '${from}'` : ''} ${to ? `AND rejected <= '${to}'` : ''} ) )` : ''}
					GROUP BY soli.iden, soli.cent
					ORDER BY soli.iden DESC;
					`
				})
			});

			if (response.ok) {

				const data = await response.json();
				const result = data.map(row => ({
					iden: row.iden,
					details: typeof row.details === 'string' ? JSON.parse(row.details) : row.details,
					ordes: typeof row.ordes === 'string' ? JSON.parse(row.ordes) : row.ordes,
					cent: row.cent,
					cc_name: row.cc_name,
					entry: row.entry,
					docu: row.docu,
					urge: row.urge,
					totalValue: row.totalValue,
					appr: row.appr,
					requester: row.requester,
					req_name: row.req_name,
					requested: row.requested,
					submitter: row.submitter,
					submitted: row.submitted,
					sub_name: row.sub_name,
					approver: row.approver,
					approved: row.approved,
					rejected: row.rejected,
					clos: row.clos,
					ord: row.ord,
					hasLvl1: row.hasLvl1,
					hasLvl2: row.hasLvl2,
					hasLvl3: row.hasLvl3,
					isApprOrd1: row.ord1,
					isApprOrd2: row.ord2,
					isApprOrd3: row.ord3,
				}));

				const solpedWithUniqueDetails = result.map(solp => {
					const uniqueDetailsSet = new Set();
					const uniqueDetails = solp.details.filter(detail => {
						if (!uniqueDetailsSet.has(detail.ritem_iden)) {
							uniqueDetailsSet.add(detail.ritem_iden);
							return true;
						}
						return false;
					});

					return { ...solp, details: uniqueDetails };
				});

				const solpedWithUniqueOrdes = solpedWithUniqueDetails.map(solp => {
					const uniqueDocuSet = new Set();
					const uniqueOrdes = solp.ordes.filter(order => {
						if (!uniqueDocuSet.has(order.docu)) {
							uniqueDocuSet.add(order.docu);
							return true;
						}
						return false;
					});

					return { ...solp, ordes: uniqueOrdes };
				});

				const evalStat = (solp) => {
					if (solp.rejected) return 'Rechazado';
					if (!solp.requester) return 'Borrador';
					else if (solp.clos) return 'Cerrado';
					else if (solp.details.every(detail => detail.delivered >= detail.amou)) return 'Entregado';
					else if (solp.details.every(detail => detail.pend === 0)) return 'Recibido';
					else if (solp.ordes.every(orde => orde.stat === 'C')) return 'Comprado';
					else if (solp.ordes.some(orde => orde.stat === 'O')) return 'Comprando';
					else if (solp.approver) return 'Aprobado';
					else if (solp.requested) return 'Solicitado';
				};

				const solpedWithEstado = solpedWithUniqueOrdes.map(solp => ({
					...solp,
					stat: evalStat(solp),
				}));
				setSolped(solpedWithEstado);

			}

		} catch (error) {
			console.log('Error en loadSolped ', error);
		}

	}, [filtRequDateEnd, filtRequDateStart, mail, role, user]);

	useEffect(() => {
		const timer = setTimeout(loadSolped, 500);
		return () => clearTimeout(timer);
	}, [loadSolped]);

	const loadGrous = useCallback(async () => {
		try {
			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: 'SELECT * FROM inv__grou;'
				})
			});

			if (response.ok) {
				const data = await response.json();
				setGrous(data);
			}

		} catch (error) {
			console.log('Error en loadGrous ', error);
		}

	}, [mail]);

	useEffect(() => {
		loadGrous();
	}, [loadGrous]);

	const loadCentStats = useCallback(async () => {
		try {
			let where = '';
			const conditions = ['requester IS NOT NULL'];

			if (selectedUrge) {
				conditions.push(`urge IS NOT NULL AND urge != ''`);
			}
			if (filtRequDateStart) {
				conditions.push(`requested >= '${localDateToUtcSql(filtRequDateStart, '00:00:00')}'`);
			}
			if (filtRequDateEnd) {
				conditions.push(`requested <= '${localDateToUtcSql(filtRequDateEnd, '23:59:59')}'`);
			}

			if (conditions.length > 0) {
				where = `WHERE ${conditions.join(' AND ')}`;
			}

			const response = await fetch(apiurl + 'queryAux', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({
					user: mail,
					query: `
						SELECT
						  cent.name cent_name,
						  t.total - t.closed abiertas,
						  t.total total,
						  (t.total - t.closed) / t.total porcentaje
						FROM (
						  SELECT
						    cent,
						    COUNT(*) total,
						    COUNT(clos) closed
						  FROM req__soli
						  ${where}
						  GROUP BY cent
						) t
						LEFT JOIN req__cent cent ON t.cent = cent.code
						ORDER BY porcentaje DESC
						LIMIT 20;
					`
				})
			});

			if (response.ok) {
				const data = await response.json();
				setCentStats(data);
			}

		} catch (error) {
			console.log('Error en loadCentStats ', error);
		}

	}, [mail, selectedUrge, filtRequDateStart, filtRequDateEnd]);

	useEffect(() => {
		const timer = setTimeout(loadCentStats, 500);
		return () => clearTimeout(timer);
	}, [loadCentStats]);

	const setHighChartOption = useCallback(() => {
		const estadosPendientes = ['Solicitado', 'Aprobado', 'Comprando', 'Comprado', 'Recibido', 'Entregado'];
		const otrosEstados = ['Cerrado'];

		// Aplicar filtros de CC y grupo al gráfico
		const solpedFiltered = filterSolped(solped);

		const pendientesMainData = {
			name: "Pendiente",
			y: estadosPendientes.reduce((acc, estado) => {
				const count = solpedFiltered.filter(solp => solp.stat === estado).length;
				return acc + count;
			}, 0),
			color: '#09F',
			drilldown: 'Pendientes'
		};

		const otrosData = otrosEstados.map(est => ({
			name: est,
			y: solpedFiltered.filter(solp => solp.stat === est).length,
			color: (est === 'Cerrado' ? '#666' : '#06B')
		}));

		const pendientesDrilldownData = {
			id: "Pendientes",
			name: "Pendientes",
			data: estadosPendientes.map(estado => ({
				name: estado,
				y: solpedFiltered.filter(solp => solp.stat === estado).length,
				color: (estado === 'Solicitado' ? '#C00' : (estado === 'Aprobado' ? '#F60' : (estado === 'Comprando' ? '#FC0' : (estado === 'Recibido' ? '#06B' : (estado === 'Entregado' ? '#939' : '#0A2')))))
			})),
			events: {
				click: function (event) {
					setSelectedState(event.point.name);
				}
			}
		};

		const charOptions = {
			chart: {
				type: 'pie',
				height: 340,
				events: {
					drilldown: function () { drilledRef.current = true; },
					drillup: function () { drilledRef.current = false; },
					load: function () {
						const chart = this;
						if (drilledRef.current) {
							setTimeout(() => {
								const point = chart.series[0] && chart.series[0].points.find(p => p.drilldown);
								if (point) {
									if (point.runDrilldown) point.runDrilldown();
									else point.doDrilldown();
								}
							}, 0);
						}
					}
				}
			},
			title: {
				text: 'Estado de solicitudes'
			},
			series: [{
				name: "Estados",
				data: [pendientesMainData, ...otrosData],
				size: '100%',
				events: {
					click: function (event) {
						setSelectedState(event.point.name);
					}
				}
			}],
			drilldown: {
				series: [pendientesDrilldownData],
			},
			tooltip: {
				headerFormat: '',
				pointFormat: '<b>{point.name}</b>: {point.y}<br/>'
			},
			plotOptions: {
				pie: {
					cursor: 'pointer',
					dataLabels: {
						enabled: true,
						format: '{point.name}: {point.y}',
						distance: -50,
						style: {
							color: 'white',
							textOutline: 'none',
							textDecoration: 'none'
						}
					},
					showInLegend: false
				}
			}
		};

		setOptions(charOptions);
	}, [solped, filterSolped]);

	useEffect(() => {
		if (solped.length > 0) {
			setHighChartOption();
			setCurrentPage(1);
		}
	}, [setHighChartOption, solped]);


	return (
		<>
			<style>{
				`g.highcharts-data-labels.highcharts-series-0.highcharts-pie-series.highcharts-tracker > g.highcharts-label.highcharts-data-label.highcharts-data-label-color-0.highcharts-drilldown-data-label > text {
					color: white !important;
					fill: white !important;
					text-decoration: none !important;
					border: 1px solid black !important;
				
				}
				.highcharts-credits{
					display: none !important;
				}
			`}</style>
			<form>
				<main>
					<h1 data-icon=''>Estadísticas</h1>
					<nav>
						<section>
							<article>
							{!solped ? ( // Mostrar la capa de carga mientras se están cargando los datos
								<p>Cargando...</p>
							) : (
								options.series && <HighchartsReact key={`${selectedCent}-${selectedGrou}-${selectedUrge}`} highcharts={Highcharts} options={options} />
							)}
							</article>
						</section>
						<section>
							<article>
								<StateChangesByMonth mail={mail} role={role} user={user} from={filtRequDateStart || ''} to={filtRequDateEnd || ''} filterSolped={filterSolped} />
							</article>
						</section>
						<section>
							<article>
								<TimeBetweenStates rows={filterSolped(solped)} />
							</article>
						</section>
					</nav>
					{
						options.series && paginatedSolped && (
							<nav>
								<section className="wide">
									<article>
									<h2>
									<div>{(!selectedState ? 'Pendiente' : selectedState)}</div>
									<div>
								 	   	<select value={selectedGrou} onChange={(e) => { setSelectedGrou(e.target.value); }}>
								 	   		<option value="">GRUPO</option>
								 	   		{
								 	   			grous.map((g, index) => (
								 	   				<option key={index} value={g.numb}>{g.grou}</option>
								 	   			))
								 	   		}
								 	   	</select>
									</div>
									</h2>
									<table>
										<tbody>
											{
												paginatedSolped.map((detail, index) => (
													<tr key={index} >
														<td>{detail.item_code} </td>
														<td className='ellipsis' > {detail.item_name} </td>
														<td>
															{detail.pend + '/' + detail.amou}
														</td>
													</tr>))
											}
										</tbody>
									</table>
									<Paginator totalItems={totalFilteredItems} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
									</article>
								</section>
								<section>
									<article>
										<MonthlySpend mail={mail} role={role} user={user} from={filtRequDateStart || ''} to={filtRequDateEnd || ''} filterSolped={filterSolped} />
									</article>
								</section>
							</nav>)
					}
				</main>
				<aside>
					<article>
						<h2>Acciones </h2>
						<fieldset>
							<input type="button" value="Volver a solicitudes" onClick={() => onMenuItemClick('listSolp')} />
						</fieldset>
					</article>
					<article>
						<h2>Filtros </h2>
						<fieldset>
							<legend>Solicitud</legend>
							<input type="date" value={filtRequDateStart} onChange={(e) => { setFiltRequDateStart(e.target.value); }} />
							<input type="date" value={filtRequDateEnd} onChange={(e) => { setFiltRequDateEnd(e.target.value); }} />
						</fieldset>
						<fieldset>
							<input id="urgency" type="checkbox" checked={selectedUrge ? true : false} onChange={() => { setSelectedUrge(prev => prev ? false : true); }} />
							<label htmlFor="urgency"> Solo urgencias</label>
						</fieldset>
					</article>
					<article>
						<h2>Centro de costos</h2>
						<fieldset>
							{
								centStats.map((c) => (
									<progress
										key={c.cent_name}
										className={selectedCent === c.cent_name ? 'sel' : ''}
										value={c.abiertas}
										max={c.total}
										data-label={c.cent_name}
										data-value={`${c.abiertas}/${c.total}`}
										onClick={() => { setSelectedCent(prev => prev === c.cent_name ? false : c.cent_name); }}
									></progress>
								))
							}
						</fieldset>
					</article>
				</aside>
			</form>
		</>
	);
}

export default ListStat;
